import { useEffect, useState } from "react";
import { api, TYPE_LABELS } from "@/lib/api";
import { Loader2, BarChart3, PieChart as PieIcon, TrendingUp } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie,
} from "recharts";

const COLORS = ["#2563eb", "#f97316", "#10b981", "#8b5cf6", "#ec4899", "#0ea5e9", "#f59e0b", "#6366f1"];

export default function Analytics() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  const themeData = (stats?.theme_performance || []).map((t) => ({ name: t.theme.split(" ")[0], score: t.avg_score, count: t.count }));
  const typeData = (stats?.type_distribution || []).map((t) => ({ name: TYPE_LABELS[t.type] || t.type, value: t.count }));

  return (
    <div className="max-w-6xl mx-auto" data-testid="analytics-page">
      <div className="mb-6">
        <p className="overline text-blue-600">Performance</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Analytics</h1>
        <p className="text-sm text-zinc-500 mt-1">Which emotional themes and product types drive your pipeline.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          ["Concepts", stats?.ideas, "text-purple-600"],
          ["Products", stats?.products, "text-blue-600"],
          ["Published", stats?.published, "text-emerald-600"],
          ["Open orders", (stats?.orders_pending || 0) + (stats?.orders_design || 0), "text-rose-600"],
        ].map(([l, v, c]) => (
          <div key={l} className="bg-white border border-zinc-200 rounded-lg p-5">
            <div className={`font-display text-3xl font-bold ${c}`}>{v ?? 0}</div>
            <div className="text-xs text-zinc-500 mt-1">{l}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-lg p-5">
          <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2 mb-4"><TrendingUp className="w-4.5 h-4.5 text-zinc-400" /> Avg impulse score by theme</h2>
          {themeData.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={themeData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f4" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#71717a" }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: "#71717a" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#f8f8f9" }} contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }} />
                <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                  {themeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-[280px] flex items-center justify-center text-sm text-zinc-400">Build products to see analytics.</div>}
        </div>

        <div className="bg-white border border-zinc-200 rounded-lg p-5">
          <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2 mb-4"><PieIcon className="w-4.5 h-4.5 text-zinc-400" /> Product mix</h2>
          {typeData.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={typeData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3}>
                  {typeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="h-[220px] flex items-center justify-center text-sm text-zinc-400">No products yet.</div>}
          <div className="space-y-1.5 mt-2">
            {typeData.map((t, i) => (
              <div key={t.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />{t.name}</span>
                <span className="text-zinc-500 font-medium">{t.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-5 mt-4">
        <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2 mb-4"><BarChart3 className="w-4.5 h-4.5 text-zinc-400" /> Theme leaderboard</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-400 border-b border-zinc-200">
                <th className="pb-2 font-medium overline">Theme</th>
                <th className="pb-2 font-medium overline text-right">Products</th>
                <th className="pb-2 font-medium overline text-right">Published</th>
                <th className="pb-2 font-medium overline text-right">Avg impulse</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.theme_performance || []).map((t) => (
                <tr key={t.theme} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                  <td className="py-2.5 font-medium">{t.theme}</td>
                  <td className="py-2.5 text-right text-zinc-600">{t.count}</td>
                  <td className="py-2.5 text-right text-zinc-600">{t.published}</td>
                  <td className="py-2.5 text-right font-bold text-blue-600">{t.avg_score}</td>
                </tr>
              ))}
              {(!stats?.theme_performance || !stats.theme_performance.length) && (
                <tr><td colSpan={4} className="py-8 text-center text-zinc-400">No data yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
